import { QueryClientProvider } from '@tanstack/react-query'
import { ErrorBoundary, Provider as RollbarProvider } from '@rollbar/react'
import * as React from 'react'
import { createRoot } from 'react-dom/client'
import { useTranslation } from 'react-i18next'
import { App } from './app'
import { Button } from './components/button'
import { isRollbarEnabled, rollbar, rollbarConfig } from './config/rollbar'
import { loadUmami } from './config/umami'
import './index.css'
import { queryClient } from './utils/query-client'

loadUmami()

const ErrorFallback = () => {
  const { t } = useTranslation()
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center">
      <h1 className="text-xl font-semibold">Something went wrong</h1>
      <p className="text-sm text-gray-500">
        An unexpected error occurred. Please refresh the page and try again.
      </p>
      <Button type="button" onClick={() => window.location.reload()}>
        {t('common.reload')}
      </Button>
    </div>
  )
}

const container = document.getElementById('root')
const root = createRoot(container!)

// When enabled (real token for the detected env), pass the shared instance so the
// Provider and the axios interceptor report through one Rollbar — two instances
// would each own the global uncaught handlers and double-report. When disabled
// (local/PR apps, no token) the instance has no accessToken, which the Provider's
// `instance` prop rejects (invariant: "`instance` must be a configured instance of
// Rollbar"); the `config` path accepts a token-less, disabled config instead.
const rollbarProviderProps = isRollbarEnabled ? { instance: rollbar } : { config: rollbarConfig }

root.render(
  <React.StrictMode>
    <RollbarProvider {...rollbarProviderProps}>
      <ErrorBoundary fallbackUI={ErrorFallback}>
        <QueryClientProvider client={queryClient}>
          <App />
        </QueryClientProvider>
      </ErrorBoundary>
    </RollbarProvider>
  </React.StrictMode>,
)
